/**
 * Agent Context
 *
 * Gathers scene context for the AI agent's system prompt.
 */

import type { TraceScene } from 'trace-viewer/scene';
import { outlineScene } from 'trace-viewer/editor';
import type { ObjectEntry, OutlineEntry } from 'trace-viewer/editor';
import type { SceneObject } from 'trace-viewer/scene';
import type { AssistantPins, MediaToggles } from './agent-types';

const r3 = (v: number) => Math.round(v * 1000) / 1000;

/**
 * World-space AABB for a SceneObject's mesh hierarchy.
 * Returns null if the object has no mesh content (e.g. empty transform node).
 */
function getWorldBounds(
    sceneObj: SceneObject,
): { min: [number, number, number]; max: [number, number, number] } | null {
    const { min, max } = sceneObj.transform.getHierarchyBoundingVectors(true);
    if (!isFinite(min.x) || !isFinite(max.x)) return null;
    if (min.x === max.x && min.y === max.y && min.z === max.z) return null;
    return {
        min: [r3(min.x), r3(min.y), r3(min.z)],
        max: [r3(max.x), r3(max.y), r3(max.z)],
    };
}

/**
 * Build a compact summary of the scene hierarchy for the system prompt.
 */
export function gatherSceneContext(
    traceScene: TraceScene,
    options: { overview?: boolean } = {},
): string {
    // The engine mount point is never listed nor named as a parent: it is not
    // a node of the file, cannot be addressed, and naming it in the context
    // invites the agent to parent to it (SCENE_ROOT_SPEC D8). The listing
    // starts at the document root.
    const objects = inSceneOrder(traceScene);

    // Sibling names are unique while editing, so a name can repeat across the
    // scene while the fully qualified transform path stays unique (a scene
    // loaded while playing, content inside a reference or a raw setParent can
    // still repeat a path; resolveObjectRef refuses those as ambiguous). Emit
    // the path only for names that actually repeat: it is the sole way to
    // address those objects unambiguously, and adding it everywhere would
    // inflate a context that is already the largest part of every request.
    const nameCounts = new Map<string, number>();
    for (const { object } of objects) {
        nameCounts.set(object.name, (nameCounts.get(object.name) ?? 0) + 1);
    }

    const describe = ({ object: sceneObj, path, parentPath }: ObjectEntry): Record<string, unknown> => {
        const obj = sceneObj.getSceneObjectData();
        const entry: Record<string, unknown> = { name: obj.name };
        if ((nameCounts.get(obj.name) ?? 0) > 1) {
            entry.path = path;
            entry.nameIsAmbiguous = true;
        }
        if (parentPath) entry.parent = parentPath;
        if (obj.behaviors && obj.behaviors.length > 0) {
            entry.behaviors = obj.behaviors.map(b => b['@type']);
        }
        // Include non-default transform values
        const t = obj.transform;
        if (t?.translation && (t.translation[0] !== 0 || t.translation[1] !== 0 || t.translation[2] !== 0)) {
            entry.position = t.translation;
        }
        if (t?.rotation && (t.rotation[0] !== 0 || t.rotation[1] !== 0 || t.rotation[2] !== 0 || t.rotation[3] !== 1)) {
            entry.rotation = t.rotation;
        }
        if (t?.scale && (t.scale[0] !== 1 || t.scale[1] !== 1 || t.scale[2] !== 1)) {
            entry.scale = t.scale;
        }
        if (obj.active === false) entry.active = false;
        if (obj.src) entry.src = obj.src;
        // World-space AABB — use to place children relative to this object
        // (e.g. "label above tank" → child world Y = bounds.max.y + offset).
        const bounds = getWorldBounds(sceneObj);
        if (bounds) entry.bounds = bounds;
        return entry;
    };

    const result: Record<string, unknown> = {};
    if (options.overview) {
        // Automatic context is a survey, not an implicit get_scene_structure.
        // Keep complete breadth-first levels when possible: a detailed first
        // assembly must not crowd all the other assemblies out of the overview.
        // Limits are scene-independent; full inspection stays available on
        // demand. Each object is measured as it is printed, so a root too large
        // to show is reported as omitted, never truncated into an identifier
        // that cannot be addressed.
        const described = new Map<OutlineEntry, Record<string, unknown>>();
        const overviewEntry = (outlined: OutlineEntry) => {
            const entry = describe(outlined);
            if (outlined.childCount) {
                entry.path = outlined.path;
                entry.childCount = outlined.childCount;
            }
            described.set(outlined, entry);
            return entry;
        };
        const outline = outlineScene(traceScene, {
            maxObjects: 64,
            maxSize: 20_000,
            measure: outlined => JSON.stringify(overviewEntry(outlined)).length + 1,
        });
        // Level by level, as the budget was kept.
        const shown = [...outline.entries].sort((a, b) => a.depth - b.depth);
        result.objects = shown.map(outlined => {
            const entry = described.get(outlined)!;
            if (outlined.omittedChildren) entry.omittedChildren = outlined.omittedChildren;
            return entry;
        });
        result.totalObjects = outline.total;
        result.omittedObjects = outline.total - shown.length;
        if (shown.length < outline.total) {
            result.detail = 'Overview only; omitted objects still exist. Use get_scene_tree with root, max_depth and include_paths to inspect a subtree, name_pattern or behavior_type to find objects, and get_transform for precise transforms/bounds. get_scene_structure returns the full flat inventory.';
        }
    } else {
        result.objects = objects.map(describe);
    }

    // Include camera pose so the AI knows "where the user is looking"
    const camera = traceScene.getBabylonScene().activeCamera;
    if (camera) {
        const pos = camera.position;
        const cameraInfo: Record<string, unknown> = {
            position: [r3(pos.x), r3(pos.y), r3(pos.z)],
        };
        // ArcRotateCamera has a target point; others have a forward direction
        if ('target' in camera && camera.target && typeof (camera.target as { x: number }).x === 'number') {
            const tgt = camera.target as { x: number; y: number; z: number };
            cameraInfo.target = [r3(tgt.x), r3(tgt.y), r3(tgt.z)];
        }
        result.camera = cameraInfo;
    }

    return JSON.stringify(result, null, options.overview ? undefined : 2);
}

/** Every object in the scene, in the scene's order (the order the listings print). */
function inSceneOrder(traceScene: TraceScene): OutlineEntry[] {
    const order = new Map(traceScene.getSceneObjects().map((object, index) => [object, index]));
    return outlineScene(traceScene).entries.sort((a, b) => order.get(a.object)! - order.get(b.object)!);
}

/**
 * Build context for the currently selected object.
 */
export function gatherSelectedObjectContext(selectedObject: SceneObject): string {
    const data = selectedObject.getSceneObjectData();
    const result: Record<string, unknown> = {
        name: data.name,
        path: selectedObject.getPath(),
        behaviors: data.behaviors,
        transform: data.transform,
    };
    const bounds = getWorldBounds(selectedObject);
    if (bounds) result.bounds = bounds;
    return JSON.stringify(result, null, 2);
}

/**
 * Body of the "## Pinned Choices" prompt section: the user's pins and
 * switched-off generation types, as instructions. Unpicked choices use the
 * library as a guide; wrapper/style/set picks are required.
 */
export function formatPinnedChoices(pins: AssistantPins, toggles: MediaToggles): string {
    const lines: string[] = [];
    if (pins.template) lines.push(`- Template/Layout (GUIDE): start from template "${pins.template}". You may adapt, combine or extend it; when the prompt clearly asks for free 3D, you may decline it and explain why instead of calling place_template.`);
    else lines.push('- Library (GUIDE): choose from the library as a head start. You may adapt, combine or extend it; when the prompt clearly asks for free 3D, build freely instead of forcing a page template.');
    if (pins.style) lines.push(`- Style (MUST): use style "${pins.style}" (place_template and stage_display), and carry it into any free-3D build description. Do not choose another style.`);
    if (pins.set) lines.push(`- Set/Form (MUST): use set "${pins.set}" for stage_display. Do not choose another set.`);
    if (pins.wrapper) lines.push(`- Wrapper (MUST): build inside wrapper "${pins.wrapper}", using stage_display to wrap the content root. Do not choose another wrapper.`);
    if (!toggles.images) lines.push('- Images are OFF: do not generate images (no generate_image, no image() in build_spatial).');
    if (!toggles.models3d) lines.push('- 3D models are OFF: do not generate 3D models.');
    if (!toggles.materials) lines.push('- Materials are OFF: do not generate materials.');
    return `The assistant's generation controls distinguish required picks from guides. Required picks are MUSTs: never silently swap them. If a required pick genuinely conflicts with the prompt, explain the incompatibility. Template/Layout picks are GUIDE starting points.\n${lines.join('\n')}`;
}

/**
 * Read script source from the ResourceManager.
 */
export async function getScriptSource(
    traceScene: TraceScene,
    typeName: string
): Promise<string | null> {
    const scriptsMap = traceScene.scriptsMap;
    const assetPath = scriptsMap?.[typeName];
    if (!assetPath) return null;

    const resources = traceScene.resources;
    const normalizedPath = assetPath.replace(/^\.\//, '');
    const blob = resources.getAsset(normalizedPath) ?? resources.getAsset(assetPath);
    if (!blob) return null;

    return blob.text();
}

/**
 * Get list of all registered script type names.
 */
export function getAvailableScriptTypes(traceScene: TraceScene): string[] {
    const scriptsMap = traceScene.scriptsMap;
    if (!scriptsMap) return [];
    return Object.keys(scriptsMap);
}
